import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Package, Mail, Phone, MapPin, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const OrderSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const order = location.state?.order;

  if (!order) {
    navigate('/');
    return null;
  }

  const statusSteps = [
    { label: 'Order Placed', status: 'paid' },
    { label: 'Processing', status: 'processing' },
    { label: 'Shipped', status: 'shipped' },
    { label: 'Delivered', status: 'delivered' }
  ];

  const currentStepIndex = statusSteps.findIndex(step => step.status === order.status);

  return (
    <div className="min-h-screen bg-dark-primary">
      <div className="container mx-auto px-6 py-20">
        <motion.div
          className="max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Success Header */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="inline-block mb-6"
            >
              <CheckCircle className="w-24 h-24 text-green-400" />
            </motion.div>
            <motion.h1 
              className="text-5xl font-bold text-white mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              Thank You for Your Order! 🎉
            </motion.h1>
            <motion.p 
              className="text-xl text-gray-400"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              Your order has been successfully placed and confirmed
            </motion.p>
          </div>

          {/* Order Details Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Card className="bg-dark-secondary border-gold/20 mb-8">
              <CardContent className="p-8">
                <div className="flex items-center justify-between mb-6 pb-6 border-b border-gold/10">
                  <div>
                    <p className="text-gray-400 text-sm">Order Number</p>
                    <p className="text-2xl font-bold text-gold">{order.order_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-400 text-sm">Order Total</p>
                    <p className="text-2xl font-bold text-white">₹{order.total_amount.toLocaleString('en-IN')}</p>
                  </div>
                </div>

                {/* Order Status */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Package className="w-6 h-6 text-gold" />
                    Order Status
                  </h3>
                  <div className="relative">
                    <div className="flex justify-between">
                      {statusSteps.map((step, index) => (
                        <div key={step.status} className="flex flex-col items-center flex-1">
                          <div 
                            className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                              index <= currentStepIndex
                                ? 'bg-gold border-gold text-dark-primary'
                                : 'bg-dark-primary border-gray-600 text-gray-600'
                            }`}
                          >
                            {index <= currentStepIndex ? '✓' : index + 1}
                          </div>
                          <p className={`text-xs mt-2 text-center ${
                            index <= currentStepIndex ? 'text-gold' : 'text-gray-500'
                          }`}>
                            {step.label}
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-700 -z-10" />
                  </div>
                </div>

                {/* Customer & Shipping Info */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                      <Mail className="w-5 h-5 text-gold" />
                      Contact Information
                    </h3>
                    <div className="space-y-2 text-gray-300">
                      <p>{order.customer.name}</p>
                      <p>{order.customer.email}</p>
                      <p className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        {order.customer.phone}
                      </p>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-gold" />
                      Shipping Address
                    </h3>
                    <div className="text-gray-300">
                      <p>{order.customer.address.street}</p>
                      <p>{order.customer.address.city}, {order.customer.address.state}</p>
                      <p>{order.customer.address.pincode}</p>
                      <p>{order.customer.address.country}</p>
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                <div className="mt-8 pt-6 border-t border-gold/10">
                  <h3 className="text-lg font-bold text-white mb-4">Order Items</h3>
                  {order.items.map((item, index) => (
                    <div key={index} className="flex items-center gap-4 mb-4">
                      {item.image_url && (
                        <img 
                          src={item.image_url} 
                          alt={item.product_name}
                          className="w-16 h-16 object-cover rounded-lg border border-gold/20"
                        />
                      )}
                      <div className="flex-1">
                        <p className="text-white font-semibold">{item.product_name}</p>
                        <p className="text-gray-400 text-sm">Quantity: {item.quantity}</p>
                      </div>
                      <p className="text-gold font-bold">₹{item.price.toLocaleString('en-IN')}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <Button
              onClick={() => navigate('/')}
              className="bg-gold hover:bg-gold-dark text-dark-primary font-bold px-8 py-6"
            >
              Continue Shopping
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>

          {/* Customer Support Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="mt-8"
          >
            <Card className="bg-gradient-to-r from-gold/10 to-transparent border-gold/30">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-4 text-center">Need Help with Your Order?</h3>
                <p className="text-gray-300 text-center mb-6">Our team is here to assist you with any questions or concerns</p>
                <div className="grid md:grid-cols-2 gap-6">
                  <a 
                    href="mailto:info@madtoys.in"
                    className="flex items-center gap-4 p-4 bg-dark-primary/50 rounded-lg hover:bg-dark-primary transition-all border border-gold/20 hover:border-gold/40"
                  >
                    <div className="bg-gold/20 p-3 rounded-full">
                      <Mail className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Email Us</p>
                      <p className="text-white font-semibold">info@madtoys.in</p>
                    </div>
                  </a>
                  <a 
                    href="tel:+919137044474"
                    className="flex items-center gap-4 p-4 bg-dark-primary/50 rounded-lg hover:bg-dark-primary transition-all border border-gold/20 hover:border-gold/40"
                  >
                    <div className="bg-gold/20 p-3 rounded-full">
                      <Phone className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Call Us</p>
                      <p className="text-white font-semibold">+91-9137044474</p>
                    </div>
                  </a>
                </div>
                <p className="text-center text-gray-400 text-sm mt-4">
                  Available Mon-Sat, 10 AM - 7 PM IST
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Email Confirmation Notice */}
          <motion.div
            className="mt-8 text-center text-gray-400 text-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            <p>A confirmation email has been sent to <span className="text-gold">{order.customer.email}</span></p>
            <p className="mt-2">Please keep your order number <span className="text-gold font-semibold">{order.order_number}</span> for reference.</p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
